import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { brandConfig } from 'src/config';
import { CheckEnvironment, SupportedEnvironments } from 'src/config-global';
import { cloud_updateUserBrand } from 'src/utils/mrr/cloudFunctions';
import { AUTH, CLOUD_FUNCTIONS } from './FirebaseContext';
import { useAuthContext } from './useAuthContext';

/**
 * Custom token handler for domainA - processes custom Firebase auth tokens from URL parameters
 * This is ONLY active in any dev environment (not just local) for cross-domain authentication
 */
export function useCustomTokenHandler() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isProcessingToken, setIsProcessingToken] = useState(false);
  const [tokenProcessed, setTokenProcessed] = useState(false);
  const { isAuthenticated, isInitialized } = useAuthContext();

  useEffect(() => {
    // Only process in dev mode and if not already authenticated
    if (!CheckEnvironment(SupportedEnvironments.Dev)) {
      return;
    }

    if (tokenProcessed) {
      return;
    }

    if (isProcessingToken) {
      return;
    }

    if (isAuthenticated) {
      return;
    }

    const customToken = searchParams.get('token');
    const email = searchParams.get('email');

    if (customToken && email) {
      setIsProcessingToken(true);

      const processCustomToken = async () => {
        try {
          // CRITICAL: Update brand FIRST so the custom token inherits correct claims
          const brandRequest = httpsCallable(CLOUD_FUNCTIONS, cloud_updateUserBrand);
          await brandRequest({
            email: email,
            brand: brandConfig.brandCode,
          });

          // Sign in with the custom token (which now has the correct brand claims)
          const userCredential = await signInWithCustomToken(AUTH, customToken);

          // Clean up URL parameters to remove the token
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('token');
          newSearchParams.delete('email');

          // Update URL without the sensitive token parameter
          setSearchParams(newSearchParams, { replace: true });

          setTokenProcessed(true);
        } catch (error) {
          console.error('Failed to sign in with custom token or update brand:', error);

          // Clean up URL parameters even on error to remove the token
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('token');
          newSearchParams.delete('email');
          setSearchParams(newSearchParams, { replace: true });
        } finally {
          setIsProcessingToken(false);
        }
      };

      processCustomToken();
    }
  }, [
    searchParams,
    setSearchParams,
    tokenProcessed,
    isProcessingToken,
    isAuthenticated,
    isInitialized,
  ]);

  return {
    isProcessingToken,
    tokenProcessed,
  };
}

/**
 * Component wrapper for custom token handling
 * This should be placed high in the component tree to intercept tokens early
 */
export function CustomTokenHandler({ children }: { children: React.ReactNode }) {
  const { isProcessingToken, tokenProcessed } = useCustomTokenHandler();

  // Show loading while processing token (optional)
  if (isProcessingToken) {
    return <div>Processing DEV authentication...</div>;
  }

  return <>{children}</>;
}
